/**
 * Attach a screenshot to feedback or a bug report.
 *
 * Two steps the tester never sees as two: register the image with the API
 * (which checks type, size and capability, and answers with a short-lived
 * write-only SAS), then PUT the bytes straight to Blob storage. The Function
 * App never carries the image. A failed upload removes the chip rather than
 * leaving a dangling id on the submission.
 */

import { useRef, useState } from 'react'
import { api, ApiError } from '@/lib/api'

export interface AttachedScreenshot {
  id: string
  name: string
}

export function ScreenshotAttach({
  attached,
  onChange,
  disabled,
}: {
  attached: AttachedScreenshot[]
  onChange: (next: AttachedScreenshot[]) => void
  disabled?: boolean
}) {
  const input = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  async function pick(file: File) {
    setUploading(true)
    setProblem(null)
    try {
      const registered = await api.registerScreenshot({
        fileName: file.name,
        contentType: file.type,
        byteSize: file.size,
      })
      const put = await fetch(registered.upload.url, {
        method: registered.upload.method,
        headers: registered.upload.headers,
        body: file,
      })
      if (!put.ok) throw new Error(`upload failed: ${put.status}`)
      onChange([...attached, { id: registered.screenshot.id, name: file.name }])
    } catch (caught) {
      setProblem(
        caught instanceof ApiError ? caught.message : 'The screenshot could not be uploaded. Try again.',
      )
    } finally {
      setUploading(false)
      if (input.current) input.current.value = ''
    }
  }

  return (
    <div className="fb-attach">
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) void pick(file)
        }}
      />
      {attached.length > 0 && (
        <ul className="fb-attach__chips">
          {attached.map((shot) => (
            <li key={shot.id}>
              <span>{shot.name}</span>
              <button
                type="button"
                aria-label={`Remove ${shot.name}`}
                onClick={() => onChange(attached.filter((entry) => entry.id !== shot.id))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {problem && (
        <p className="fb-panel__error" role="alert">
          {problem}
        </p>
      )}
      {attached.length < 3 && (
        <button
          className="fb-link"
          type="button"
          disabled={disabled || uploading}
          onClick={() => input.current?.click()}
        >
          {uploading ? 'Uploading…' : attached.length > 0 ? 'Attach another screenshot' : '📎 Attach a screenshot'}
        </button>
      )}
    </div>
  )
}
